/**
 * Tutor information-meeting form section.
 * Session picker unless already marked participated; checkbox stamps actor + timestamp.
 */
import * as React from 'react';
import { Info } from 'lucide-react';
import { Checkbox } from '@/components/forms/Checkbox';
import { DateTimePicker } from '@/components/forms/DateTimePicker';
import { MeetingSelector } from '@/components/common/MeetingSelector';
import {
  buildInformationMeetingParticipationPatch,
  shouldShowInformationMeetingParticipationFields,
} from '@/components/tutors/remote/tutor-form-utils';

/** Props for {@link TutorInformationMeetingSection}. */
type Props = Readonly<{
  formData: Record<string, any>;
  onChange: (key: string, value: any) => void;
  userId: string;
  companyId: string;
  mode: 'create' | 'edit';
  user: any;
}>;

/** Tutor information-meeting form section. */
export function TutorInformationMeetingSection({
  formData,
  onChange,
  userId,
  companyId,
  mode,
  user,
}: Props) {
  const showParticipationFields = shouldShowInformationMeetingParticipationFields(formData);

  function handleParticipatedChange(checked: boolean): void {
    const patch = buildInformationMeetingParticipationPatch(checked, formData, user);
    Object.entries(patch).forEach(([key, value]) => onChange(key, value));
  }

  function handleSessionIdChange(id: string | null): void {
    onChange('sessionId', id);
  }

  function handleParticipatedAtChange(value: string): void {
    onChange('participatedAt', value);
  }

  return (
    <div className="form-section">
      <h4 className="form-section-title">
        <Info className="w-4 h-4" />
        Réunion d'information
      </h4>
      <div className="space-y-4">
        {!formData.hasParticipated && (
          <MeetingSelector
            type="information_meeting"
            userId={userId ?? ''}
            companyId={companyId ?? ''}
            value={formData.sessionId ?? null}
            onChange={handleSessionIdChange}
            mode={mode}
          />
        )}
        {formData.hasParticipated && (
          <p className="text-sm text-muted-foreground">
            Participation déjà effectuée — inscription à une séance non requise.
          </p>
        )}
        {showParticipationFields && (
          <div className="grid grid-cols-2 gap-4 items-end">
            <Checkbox
              label="A participé"
              checked={formData.hasParticipated === true}
              onCheckedChange={handleParticipatedChange}
              size="sm"
            />
            <DateTimePicker
              label="Date de participation"
              value={formData.participatedAt ?? ''}
              onChange={handleParticipatedAtChange}
            />
          </div>
        )}
      </div>
    </div>
  );
}
